import { Navigate, Outlet, useLocation, useSearchParams } from 'react-router';
import { useSessionStore } from '@/state/sessionStore';
import { NotAvailablePage, Splash } from './pages/StatusPages';

/** A `next=` target only if it is a path on this site (never another origin). */
export function safeNext(next: string | null): string {
  if (!next || !next.startsWith('/') || next.startsWith('//') || next.startsWith('/\\')) {
    return '/';
  }
  return next;
}

/**
 * 🔒 routes (contracts/web-routes.md): signed-out visitors go to sign-in and
 * come back afterwards (WEB-008); a pending phone verification comes first
 * (FR-003, as Android's RootNavigator does).
 */
export function RequireSession() {
  const status = useSessionStore((s) => s.status);
  const sessionEnded = useSessionStore((s) => Boolean(s.endedReason));
  const signedOutByMember = useSessionStore((s) => Boolean(s.signedOutByMember));
  const phonePending = useSessionStore((s) => Boolean(s.member?.phoneVerificationPending));
  const location = useLocation();

  if (status === 'unknown') {
    return <Splash />;
  }
  // A first visit sees the welcome slides, and so does a member who just signed out (as
  // Android): no `next`, which would lead the next person to sign in back to that page.
  // A session that just ended goes to sign-in, which says why.
  if (status === 'signedOut' && !sessionEnded && (signedOutByMember || location.pathname === '/')) {
    return <Navigate to="/welcome" replace />;
  }
  if (status === 'signedOut') {
    const next = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }
  if (phonePending && location.pathname !== '/verify-phone') {
    return <Navigate to="/verify-phone" replace />;
  }
  return <Outlet />;
}

/** 🛡 routes: administrators only. Members see "Not available" and no admin request is made (WEB-022). */
export function RequireAdmin() {
  const isAdmin = useSessionStore((s) => Boolean(s.member?.isAdmin));
  return isAdmin ? <Outlet /> : <NotAvailablePage />;
}

/** Sign-in, sign-up and password reset: a signed-in member is sent on to `next` (or home). */
export function PublicOnly() {
  const status = useSessionStore((s) => s.status);
  const [params] = useSearchParams();
  if (status === 'unknown') {
    return <Splash />;
  }
  if (status === 'signedIn') {
    return <Navigate to={safeNext(params.get('next'))} replace />;
  }
  return <Outlet />;
}
